import { CopyableTableCell } from "@/components/typography/dfp-typography";
import { DB_ORG } from "@/constants/db";
import { approveSubmission } from "@/services/submission-service";
import { type IOrganization } from "@/stores/user-store";
import { parseDateToLocalString } from "@/utils/transforms";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Skeleton, Space, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { type ReactNode } from "react";
import { type SubmissionTableState } from "../SubmissionsTable";
interface AssetType {
	name: string;
	id: string | number;
}

/**
 * Helper to unify skeleton logic:
 * Returns a <Skeleton.Input> if isLoading=true, else returns whatever "real" content you pass in.
 */
function renderSkeletonOr(
	isLoading: boolean,
	realContent: ReactNode,
	skeletonWidth = 120, // default width for Skeleton.Input
) {
	if (isLoading) {
		return <Skeleton.Input style={{ width: skeletonWidth }} active />;
	}
	return realContent;
}

export const getOrgColumns = (
	organization: IOrganization,
	state: SubmissionTableState | undefined,
	onApprove?: () => void,
): ColumnsType<any> => {
	// ----------------------------------
	//  Default columns for "all" orgs
	// ----------------------------------
	const columns: ColumnsType<any> = [
		{
			title: "Asset Tag",
			dataIndex: "asset_tag",
			key: "asset_tag",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			width: "15%",
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text} />,
					80, // optional skeleton width
				),
		},
		{
			title: "Asset Type",
			dataIndex: ["asset_type", "name"],
			key: "asset_type_name",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			width: "20%",
			filters: state?.filterOptions?.assetTypes
				? state.filterOptions.assetTypes.map((type: string) => ({
						text: type,
						value: type,
					}))
				: undefined,
			filteredValue: state?.filters?.asset_type_name
				? (state.filters.asset_type_name as string[])
				: undefined,
			filterSearch: true,
			render: (text, record) =>
				renderSkeletonOr(record.isLoading, <CopyableTableCell text={text} />),
		},
		{
			title: "Address",
			dataIndex: "address",
			key: "address1",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			width: "30%",
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text || ""} />,
				),
		},
		{
			title: "Submitting User",
			dataIndex: "submitting_user",
			key: "submitting_user",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text || ""} />,
				),
		},
		{
			title: "Submission Date",
			dataIndex: "submitted_date",
			key: "submitted_date",
			sorter: true,
			defaultSortOrder: "descend",
			sortDirections: ["descend", "ascend", "descend"],
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell
						text={text ? parseDateToLocalString(text) || "No Date" : "No Date"}
					/>,
					100,
				),
		},
	];

	// ----------------------------------
	//  PCA-specific columns
	// ----------------------------------
	const PCAColumns: ColumnsType<any> = [
		{
			title: "Address",
			dataIndex: "address",
			key: "address1",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			width: "30%",
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text || ""} />,
				),
		},
		{
			title: "Submitting User",
			dataIndex: "submitting_user",
			key: "submitting_user",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text || ""} />,
				),
		},
		{
			title: "Submission Date",
			dataIndex: "submitted_date",
			key: "submitted_date",
			sorter: true,
			defaultSortOrder: "descend",
			sortDirections: ["descend", "ascend", "descend"],
			render: (text: string | undefined, record) =>
				renderSkeletonOr(
					record.isLoading,
					text ? (
						<CopyableTableCell
							text={parseDateToLocalString(text) || "No Date"}
						/>
					) : (
						<CopyableTableCell text="No Date" />
					),
					100,
				),
		},
	];

	// ----------------------------------
	//  Cushman columns
	// ----------------------------------
	const cushmanColumns: ColumnsType<any> = [
		{
			title: "Site Name",
			dataIndex: "address",
			key: "address",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			width: "30%",
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text || ""} />,
				),
		},
		{
			title: "Submitting User",
			dataIndex: "submitting_user",
			key: "submitting_user",
			sorter: true,
			sortDirections: ["descend", "ascend", "descend"],
			render: (text, record) =>
				renderSkeletonOr(
					record.isLoading,
					<CopyableTableCell text={text || ""} />,
				),
		},
		{
			title: "Submission Date",
			dataIndex: "submitted_date",
			key: "submitted_date",
			sorter: true,
			defaultSortOrder: "descend",
			sortDirections: ["descend", "ascend", "descend"],
			render: (text: string | undefined, record) =>
				renderSkeletonOr(
					record.isLoading,
					text ? (
						<CopyableTableCell
							text={parseDateToLocalString(text) || "No Date"}
						/>
					) : (
						<CopyableTableCell text="No Date" />
					),
					100,
				),
			width: "20%",
		},
		{
			title: "Approved",
			dataIndex: "is_approved",
			key: "is_approved",
			align: "center",
			width: "12%",
			render: (text, record) => {
				if (record.isLoading) {
					return <Skeleton.Input style={{ width: 60 }} active />;
				}
				if (text) {
					return <CheckOutlined className="green" />;
				}
				return <CloseOutlined className="red" />;
			},
		},
	];

	// ----------------------------------
	//  Org 28 specific: Add Actions column for pending approvals and approved
	// ----------------------------------
	if (
		organization?.id === 28 &&
		(state?.selectedFormMode === "pending_approval" ||
			state?.selectedFormMode === "approved")
	) {
		const handleApprove = async (submissionId: number) => {
			try {
				await approveSubmission(submissionId, { isRegularApproval: true });
				message.success("Submission approved successfully");
				// Refresh data
				if (onApprove) {
					onApprove();
				}
			} catch (error: any) {
				message.error(`Failed to approve submission: ${error.message}`);
			}
		};

		const actionsColumn = {
			title: "Actions",
			key: "actions",
			width: "15%",
			align: "center" as const,
			render: (_: any, record: any) => {
				if (record.isLoading) {
					return <Skeleton.Input style={{ width: 120 }} active />;
				}

				const isPendingApproval =
					state?.selectedFormMode === "pending_approval";

				return (
					<Space size="small">
						{isPendingApproval && (
							<Button
								type="primary"
								size="small"
								onClick={(e) => {
									e.stopPropagation(); // Prevent row click
									handleApprove(record.id);
								}}
							>
								Approve
							</Button>
						)}
					</Space>
				);
			},
		};

		// Add actions column to the appropriate column set
		if (state?.selectedForm === 7) {
			return [...PCAColumns, actionsColumn];
		}

		switch (organization?.external_firebase_id) {
			case DB_ORG.CUSHMANWAKEFIELD:
				return [...cushmanColumns, actionsColumn];
			default:
				return [...columns, actionsColumn];
		}
	}

	// ----------------------------------
	//  Decide which columns to return
	// ----------------------------------
	if (state?.selectedForm === 7) {
		// For form #7 => return PCA columns
		return PCAColumns;
	}

	switch (organization?.external_firebase_id) {
		case DB_ORG.CUSHMANWAKEFIELD:
			return cushmanColumns;
		default:
			return columns;
	}
};
